/**
 * frontend/src/hooks/useSettings.js
 *
 * React hook that subscribes to SettingsStore.
 * Components use this — they never touch SettingsStore directly.
 *
 * Usage:
 *   const { settings, set, reset } = useSettings();
 *   const { settings: { theme } } = useSettings('theme'); // subscribe to one key
 */

import { useState, useEffect, useCallback } from 'react';
import SettingsStore from '../store/SettingsStore';

export function useSettings() {
    const [settings, setSettings] = useState(() => SettingsStore.get());

    useEffect(() => {
        const unsubscribe = SettingsStore.subscribe(setSettings);
        return unsubscribe;
    }, []);

    const set = useCallback((key, value) => SettingsStore.set(key, value), []);
    const setMany = useCallback((updates) => SettingsStore.setMany(updates), []);
    const reset = useCallback(() => SettingsStore.reset(), []);

    return { settings, set, setMany, reset };
}

/**
 * Optimized hook for a single setting — only re-renders when that key changes.
 * Use for settings that are read in many components (theme, fontSize etc).
 */
export function useSetting(key) {
    const [value, setValue] = useState(() => SettingsStore.get(key));

    useEffect(() => {
        const unsubscribe = SettingsStore.subscribe((all) => {
            setValue(all[key]);
        });
        return unsubscribe;
    }, [key]);

    const set = useCallback((v) => SettingsStore.set(key, v), [key]);

    return [value, set];
}
